import {managedWorkspace} from "../platform/managed";
import { apiRequest } from "../api/client";
import type { CRMAccess, CRMOptions, CRMUser, SessionContext, SimpleBillingAccess, Tenant, WorkspaceProduct } from "../api/types";

interface UserEnvelope {
  user: CRMUser;
  tenant?: Tenant | null;
  features?: unknown;
  feature_tenant_id?: unknown;
  workspace_product?: unknown;
  simple_billing_access?: unknown;
  crm_access?: unknown;
}

interface TenantEnvelope {
  tenants: Tenant[];
}

interface UsersEnvelope {
  users: CRMUser[];
}

const EMPTY_OPTIONS: CRMOptions = {
  lead_sources: [],
  product_interests: [],
  business_categories: [],
  lead_statuses: [],
  lead_priorities: [],
  user_roles: [],
  follow_up_statuses: [],
  follow_up_types: [],
  social_platforms: [],
};

export function parseSessionFeatureCodes(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(
    value
      .filter((item): item is string => typeof item === "string")
      .map((item) => item.trim())
      .filter(Boolean),
  )];
}

function parseFeatureTenantId(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function parseWorkspaceProduct(value: unknown): WorkspaceProduct | null {
  return value === "CRM" || value === "SIMPLE_BILLING" ? value : null;
}

export function parseSimpleBillingAccess(value: unknown): SimpleBillingAccess | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  const state = source.state;
  if (state !== "full" && state !== "grace" && state !== "read_only" && state !== "locked") {
    return null;
  }
  return {
    state,
    canRead: state !== "locked",
    canWrite: state === "full" || state === "grace",
    reason: typeof source.reason === "string" ? source.reason : null,
    message: typeof source.message === "string" ? source.message : null,
    graceEndsAt: typeof source.grace_ends_at === "string" ? source.grace_ends_at : null,
    currentPeriodEnd: typeof source.current_period_end === "string" ? source.current_period_end : null,
    cancelAtPeriodEnd: source.cancel_at_period_end === true,
  };
}

export function parseCRMAccess(value: unknown): CRMAccess | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  if (source.state !== "active" && source.state !== "unactivated") return null;
  return {
    state: source.state,
    canUseCrm: source.can_use_crm === true,
    reason: typeof source.reason === "string" ? source.reason : null,
    grandfathered: source.grandfathered === true,
  };
}

export async function loadSessionContext(selectedTenantId?: string | null): Promise<SessionContext> {
  const managed=managedWorkspace();
  if(managed && selectedTenantId && selectedTenantId!==managed.context.source_workspace_id)throw new Error("Choose your business again.");
  const tenantQuery = selectedTenantId?.trim()
    ? `?tenant_id=${encodeURIComponent(selectedTenantId.trim())}`
    : "";
  const me = await apiRequest<UserEnvelope>(`/api/auth/me${tenantQuery}`);
  const user = me.user;
  const sessionTenant = me.tenant || null;
  const features = parseSessionFeatureCodes(me.features);
  const featureTenantId = parseFeatureTenantId(me.feature_tenant_id);
  const workspaceProduct = parseWorkspaceProduct(me.workspace_product);
  const simpleBillingAccess = parseSimpleBillingAccess(me.simple_billing_access);
  const crmAccess = parseCRMAccess(me.crm_access);
  if (user.must_change_password) {
    return {
      user,
      tenant: sessionTenant,
      tenants: [],
      users: [],
      options: EMPTY_OPTIONS,
      features,
      featureTenantId,
      workspaceProduct,
      simpleBillingAccess,
      crmAccess,
    };
  }
  const options = await apiRequest<CRMOptions>("/api/options");
  const [tenantResponse, userResponse] = await Promise.all([
    user.role === "staff"
      ? Promise.resolve<TenantEnvelope>({ tenants: [] })
      : apiRequest<TenantEnvelope>("/api/tenants"),
    apiRequest<UsersEnvelope>("/api/users"),
  ]);
  const tenants = tenantResponse.tenants || [];
  const users = userResponse.users || [];
  const tenant = sessionTenant
    || tenants.find((item) => item.id === featureTenantId)
    || tenants.find((item) => item.id === user.tenant_id)
    || tenants[0]
    || null;

  return {
    user,
    tenant,
    tenants,
    users,
    options,
    features,
    featureTenantId,
    workspaceProduct,
    simpleBillingAccess,
    crmAccess,
  };
}
